import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';

import { dateNormalizer, errorNormalizer } from '../../../../shared/Helpers/functions';
import { parseWallClockTime } from '../../../../shared/Helpers/timezones';
import { CustomButton, Picker, Text } from '../../../../shared/uiComponents';
import { EditSvg } from '../../../../shared/Assets/svgIcons';
import { Date } from '../../../../shared/Assets/sessionDetailsSVGs';
import { TEXT } from '../../../../shared/Helpers/colors';
import { AdminTypes } from '../../../../components/Menu';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { UpdateSessionProperties } from '../../../../redux/API/ClientAPIHelpers/sessionsProperties';
import { updateSession } from '../../../../redux/State/clientSlice/sessionSlice';
import { useResetCalendarData } from '../../Helpers/hooks';

const DateInfo = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();

	const [edit, setEdit] = useState<boolean>(false);

	const data = useSelector((state) => state.calendarHelper.infoEventData);
	const userId = useSelector((state) => state.account.user.id);

	const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs(data?.date));

	const role = useSelector((state) => state.account.role);
	const isFlagged = data?.isSessionFlagged;
	const isCSSession = role?.section.id === AdminTypes.cs ? data?.user.id === userId && isFlagged : false;
	const isAdmin = role?.section.id === AdminTypes.admin;
	const isRecurringScheduleSession = data?.isRecurringScheduleSession;

	const canEdit = !isRecurringScheduleSession && !data?.isCancelled && (isAdmin || isCSSession);

	const openEditDateHandler = () => setEdit(true);
	const closeEditDateHandler = () => setEdit(false);

	const saveDateHandler = () => {
		if (!data || !dateValue || !data.startTime || !data.endTime) return;
		const sessionId = data.sessionId;
		const newDate = dateNormalizer(dateValue);
		const startTime = `${newDate}T${parseWallClockTime(data.startTime).format('HH:mm')}`;
		const endTime = `${newDate}T${parseWallClockTime(data.endTime).format('HH:mm')}`;

		const updatedSession: UpdateSessionProperties = {
			id: sessionId,
			date: newDate,
			endTime,
			startTime,
		};

		dispatch(updateSession({ sessionId, data: updatedSession }))
			.then(unwrapResult)
			.then(() => {
				resetData();
				toast('Success');
			})
			.catch(errorNormalizer);
	};

	return (
		<div className="row">
			{edit ? (
				<div className="rowContent">
					<div className="text">
						<Picker.CustomDate label="Date of session" onChange={setDateValue} value={dateValue} />
					</div>
					<div className="action">
						<CustomButton title="Save" onClick={saveDateHandler} />
						<CustomButton title="Cancel" buttonType="secondaryGray" onClick={closeEditDateHandler} />
					</div>
				</div>
			) : (
				<>
					<div className="icon">
						<Date />
					</div>
					<div className="rowContent">
						<div className="text">
							<Text title="Date" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
							<Text title={dayjs(data?.date).format('MM/DD/YYYY')} type="psm" fontWeight="semiBold" />
						</div>
						{canEdit && (
							<div className="actionIcon" onClick={openEditDateHandler}>
								<EditSvg />
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default DateInfo;
